import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { featuredPropertiesService } from '../../services/featuredPropertiesService';
import FeaturedPropertyCard from './components/FeaturedPropertyCard';
import FeaturedPropertyModal from './components/FeaturedPropertyModal';
import PropertySelectionModal from './components/PropertySelectionModal';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import BulkOperationsPanel from './components/BulkOperationsPanel';

const FeaturedPropertiesManagementDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  
  // State management
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('display_order');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedProperties, setSelectedProperties] = useState([]);
  
  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPropertySelectionOpen, setIsPropertySelectionOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  
  // View states
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'analytics'

  // Check authentication and authorization
  const isAuthorized = user && userProfile && (userProfile?.role === 'admin' || userProfile?.role === 'agent');

  // Load featured properties
  const loadFeaturedProperties = async () => {
    setLoading(true);
    setError(null);
    setAuthError(null);
    
    try {
      const { data, error: fetchError } = await featuredPropertiesService?.getAllFeaturedProperties();
      
      if (fetchError) {
        if (fetchError?.includes('connecté') || fetchError?.includes('permissions')) {
          setAuthError(fetchError);
          return;
        }
        setError(`Erreur lors du chargement: ${fetchError}`);
        return;
      }
      
      setFeaturedProperties(data || []);
    } catch (err) {
      if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
        setAuthError('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause. Veuillez vérifier votre tableau de bord Supabase.');
        return;
      }
      setError(`Erreur inattendue: ${err?.message || 'Une erreur est survenue'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      loadFeaturedProperties();
    } else if (!authLoading && !user) {
      setLoading(false);
      setAuthError('Vous devez être connecté avec un compte administrateur ou agent pour accéder à la gestion des propriétés en vedette.');
    }
  }, [authLoading, user]);

  // Handle manual reordering (without drag-and-drop)
  const movePropertyUp = async (index) => {
    if (index === 0 || !isAuthorized) return;
    
    const updatedProperties = [...featuredProperties];
    [updatedProperties[index], updatedProperties[index - 1]] = [updatedProperties?.[index - 1], updatedProperties?.[index]];
    
    // Update display orders
    const propertiesToUpdate = updatedProperties?.map((item, idx) => ({
      ...item,
      display_order: idx + 1
    }));
    
    setFeaturedProperties(propertiesToUpdate);
    
    // Update in database
    try {
      await Promise.all([
        featuredPropertiesService?.updateDisplayOrder(propertiesToUpdate?.[index - 1]?.id, index),
        featuredPropertiesService?.updateDisplayOrder(propertiesToUpdate?.[index]?.id, index + 1)
      ]);
    } catch (err) {
      setError(`Erreur lors de la mise à jour de l'ordre: ${err?.message}`);
      loadFeaturedProperties(); // Reload to revert changes
    }
  };

  const movePropertyDown = async (index) => {
    if (index === featuredProperties?.length - 1 || !isAuthorized) return;
    
    const updatedProperties = [...featuredProperties];
    [updatedProperties[index], updatedProperties[index + 1]] = [updatedProperties?.[index + 1], updatedProperties?.[index]];
    
    // Update display orders
    const propertiesToUpdate = updatedProperties?.map((item, idx) => ({
      ...item,
      display_order: idx + 1
    }));
    
    setFeaturedProperties(propertiesToUpdate);
    
    // Update in database
    try {
      await Promise.all([
        featuredPropertiesService?.updateDisplayOrder(propertiesToUpdate?.[index]?.id, index + 1),
        featuredPropertiesService?.updateDisplayOrder(propertiesToUpdate?.[index + 1]?.id, index + 2)
      ]);
    } catch (err) {
      setError(`Erreur lors de la mise à jour de l'ordre: ${err?.message}`);
      loadFeaturedProperties(); // Reload to revert changes
    }
  };

  // Handle property actions
  const handleEdit = (property) => {
    if (!isAuthorized) {
      setAuthError('Vous devez avoir les permissions d\'administrateur ou d\'agent pour modifier les propriétés en vedette.');
      return;
    }
    setSelectedProperty(property);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!isAuthorized) {
      setAuthError('Vous devez avoir les permissions d\'administrateur ou d\'agent pour supprimer les propriétés en vedette.');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette propriété en vedette ?')) {
      return;
    }

    try {
      const { error: deleteError } = await featuredPropertiesService?.deleteFeaturedProperty(id);
      
      if (deleteError) {
        if (deleteError?.includes('permissions')) {
          setAuthError(deleteError);
          return;
        }
        setError(`Erreur lors de la suppression: ${deleteError}`);
        return;
      }
      
      await loadFeaturedProperties();
    } catch (err) {
      setError(`Erreur lors de la suppression: ${err?.message}`);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    if (!isAuthorized) {
      setAuthError('Vous devez avoir les permissions d\'administrateur ou d\'agent pour modifier le statut des propriétés en vedette.');
      return;
    }

    try {
      const { error: toggleError } = await featuredPropertiesService?.toggleFeaturedPropertyStatus(id, !currentStatus);
      
      if (toggleError) {
        if (toggleError?.includes('permissions')) {
          setAuthError(toggleError);
          return;
        }
        setError(`Erreur lors du changement de statut: ${toggleError}`);
        return;
      }
      
      await loadFeaturedProperties();
    } catch (err) {
      setError(`Erreur lors du changement de statut: ${err?.message}`);
    }
  };

  const handleModalSave = async () => {
    setIsEditModalOpen(false);
    setSelectedProperty(null);
    await loadFeaturedProperties();
  };

  const handleAddProperty = () => {
    if (!isAuthorized) {
      setAuthError('Vous devez avoir les permissions d\'administrateur ou d\'agent pour ajouter des propriétés en vedette.');
      return;
    }
    setIsPropertySelectionOpen(true);
  };

  // Filter and sort properties
  const filteredAndSortedProperties = featuredProperties
    ?.filter((property) => {
      const matchesSearch = property?.property?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          property?.custom_headline?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesFilter = filterStatus === 'all' || 
                          (filterStatus === 'active' && property?.is_active) ||
                          (filterStatus === 'inactive' && !property?.is_active);
      
      return matchesSearch && matchesFilter;
    })
    ?.sort((a, b) => {
      switch (sortBy) {
        case 'display_order':
          return (a?.display_order || 0) - (b?.display_order || 0);
        case 'created_at':
          return new Date(b?.created_at) - new Date(a?.created_at);
        case 'click_count':
          return (b?.click_count || 0) - (a?.click_count || 0);
        case 'inquiry_count':
          return (b?.inquiry_count || 0) - (a?.inquiry_count || 0);
        default:
          return 0;
      }
    });

  // Handle selection for bulk operations
  const handleSelectProperty = (propertyId) => {
    if (!isAuthorized) return;
    setSelectedProperties(prev => 
      prev?.includes(propertyId)
        ? prev?.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleSelectAll = () => {
    if (!isAuthorized) return;
    if (selectedProperties?.length === filteredAndSortedProperties?.length) {
      setSelectedProperties([]);
    } else {
      setSelectedProperties(filteredAndSortedProperties?.map(p => p?.id) || []);
    }
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-brand-earth mx-auto mb-4" />
          <p className="text-lg text-text-secondary">Chargement des propriétés en vedette...</p>
        </div>
      </div>
    );
  }

  // Authentication error state
  if (authError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-warm p-8 text-center">
          <Icon name="ShieldAlert" size={64} className="text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-text-primary mb-4">
            Accès Restreint
          </h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">{authError}</p>
          </div>
          
          {!user ? (
            <div className="space-y-4">
              <p className="text-text-secondary mb-6">
                Vous devez être connecté avec un compte administrateur ou agent pour accéder à la gestion des propriétés en vedette.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="default"
                  onClick={() => window.location.href = '/login'}
                >
                  Se connecter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = '/'}
                >
                  Retour à l'accueil
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-text-secondary mb-6">
                Votre compte ({userProfile?.role || 'client'}) ne dispose pas des permissions nécessaires pour gérer les propriétés en vedette.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={clearAuthError}
                >
                  Réessayer
                </Button>
                <Button
                  variant="default"
                  onClick={() => window.location.href = '/'}
                >
                  Retour à l'accueil
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white shadow-warm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-text-primary">
                Gestion des Propriétés en Vedette
              </h1>
              <p className="text-text-secondary mt-2">
                Gérez les propriétés affichées sur la page d'accueil
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setViewMode(viewMode === 'grid' ? 'analytics' : 'grid')}
                iconName={viewMode === 'grid' ? 'BarChart3' : 'Grid3X3'}
                disabled={!isAuthorized}
              >
                {viewMode === 'grid' ? 'Analytics' : 'Grille'}
              </Button>
              
              <Button
                variant="default"
                onClick={handleAddProperty}
                iconName="Plus"
                disabled={!isAuthorized}
                title={!isAuthorized ? 'Permissions insuffisantes' : ''}
              >
                Ajouter Propriété
              </Button>
            </div>
          </div>
          
          {/* User Info */}
          {user && (
            <div className="mt-4 flex items-center gap-2 text-sm text-text-secondary">
              <Icon name="User" size={16} />
              <span>Connecté en tant que: {userProfile?.full_name} ({userProfile?.role})</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Rechercher
              </label>
              <Input
                type="text"
                placeholder="Titre, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                iconName="Search"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Statut
              </label>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
              >
                <option value="all">Tous</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Trier par
              </label>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
              >
                <option value="display_order">Ordre d'affichage</option>
                <option value="created_at">Date de création</option>
                <option value="click_count">Clics</option>
                <option value="inquiry_count">Demandes</option>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={handleSelectAll}
                iconName="CheckSquare"
                fullWidth
                disabled={!isAuthorized}
              >
                {selectedProperties?.length === filteredAndSortedProperties?.length ? 'Désélectionner' : 'Sélectionner'} tout
              </Button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
              <span className="text-red-800">{error}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError(null)}
                iconName="X"
                className="ml-auto"
              />
            </div>
          </div>
        )}

        {/* Bulk Operations Panel */}
        {selectedProperties?.length > 0 && isAuthorized && (
          <BulkOperationsPanel
            selectedProperties={selectedProperties}
            onBulkAction={(action) => {
              // Handle bulk operations
              console.log('Bulk action:', action);
            }}
            onClose={() => setSelectedProperties([])}
          />
        )}

        {/* View Content */}
        {viewMode === 'analytics' ? (
          <AnalyticsDashboard />
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-warm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary">Total</p>
                    <p className="text-2xl font-bold text-text-primary">
                      {featuredProperties?.length || 0}
                    </p>
                  </div>
                  <Icon name="Star" size={24} className="text-brand-gold" />
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-warm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary">Actifs</p>
                    <p className="text-2xl font-bold text-green-600">
                      {featuredProperties?.filter(p => p?.is_active)?.length || 0}
                    </p>
                  </div>
                  <Icon name="CheckCircle" size={24} className="text-green-600" />
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-warm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary">Total Clics</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {featuredProperties?.reduce((sum, p) => sum + (p?.click_count || 0), 0)}
                    </p>
                  </div>
                  <Icon name="MousePointer" size={24} className="text-blue-600" />
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-warm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary">Total Demandes</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {featuredProperties?.reduce((sum, p) => sum + (p?.inquiry_count || 0), 0)}
                    </p>
                  </div>
                  <Icon name="MessageSquare" size={24} className="text-purple-600" />
                </div>
              </div>
            </div>

            {/* Properties Grid */}
            {filteredAndSortedProperties?.length === 0 ? (
              <div className="bg-white rounded-lg shadow-warm p-12 text-center">
                <Icon name="Home" size={48} className="text-text-secondary mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">
                  Aucune propriété en vedette
                </h3>
                <p className="text-text-secondary mb-6">
                  Commencez par ajouter des propriétés à mettre en vedette sur la page d'accueil.
                </p>
                <Button
                  variant="default"
                  onClick={handleAddProperty}
                  iconName="Plus"
                  disabled={!isAuthorized}
                >
                  Ajouter une propriété
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedProperties?.map((featuredProperty, index) => (
                  <div key={featuredProperty?.id} className="relative">
                    <FeaturedPropertyCard
                      featuredProperty={featuredProperty}
                      isSelected={selectedProperties?.includes(featuredProperty?.id)}
                      onSelect={() => handleSelectProperty(featuredProperty?.id)}
                      onEdit={() => handleEdit(featuredProperty)}
                      onDelete={() => handleDelete(featuredProperty?.id)}
                      onToggleStatus={() => handleToggleStatus(featuredProperty?.id, featuredProperty?.is_active)}
                      isAuthorized={isAuthorized}
                    />
                    
                    {/* Manual Reordering Controls */}
                    {isAuthorized && sortBy === 'display_order' && (
                      <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => movePropertyUp(index)}
                          disabled={index === 0}
                          className="p-1 h-8 w-8 bg-white shadow-md hover:bg-gray-50"
                          iconName="ChevronUp"
                          title="Déplacer vers le haut"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => movePropertyDown(index)}
                          disabled={index === filteredAndSortedProperties?.length - 1}
                          className="p-1 h-8 w-8 bg-white shadow-md hover:bg-gray-50"
                          iconName="ChevronDown"
                          title="Déplacer vers le bas"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {isEditModalOpen && (
          <FeaturedPropertyModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedProperty(null);
            }}
            featuredProperty={selectedProperty}
            onSave={handleModalSave}
          />
        )}

        {isPropertySelectionOpen && (
          <PropertySelectionModal
            isOpen={isPropertySelectionOpen}
            onClose={() => setIsPropertySelectionOpen(false)}
            onSave={handleModalSave}
          />
        )}
      </div>
    </div>
  );
};

export default FeaturedPropertiesManagementDashboard;